/**
 * shell/columns.ts —— **三栏列宽求解**（壳规范 §1／§1bis；**列宽唯一来源**）。
 *
 * 依据（逐字）：
 *  · 旧树真源 `102:frontend_src/src/components/AppFrame.vue`：「frame ＝ CSS Grid
 *    （`display:grid; grid-template-rows:100%; height:100%`），列宽**唯一来源**＝inline
 *    `grid-template-columns: {sidebar}px minmax(0,1fr) {rightbar}px`；旧版＝flex ＋ 子元素 width
 *    （中栏 `width` 与 `flex:1` 互相覆盖，且高度链失守）⇒ **已废**」。
 *  · `shell/shell.decl.yaml` 的 `breaks[]`（档位与各档左栏形态／右栏形态）＝**断点单源**。
 *
 * 纪律：**本件是全树唯一写列宽数字的地方**（CSS 侧不再用 flex-basis/width 决定列宽）；
 *      数字与 `styles/tokens/xg-tokens.css` 的同名 token 保持同值（由 `tests/shell.test.tsx` 断言守卫）。
 *      右栏**默认收合**（大王 20261004 裁）；`push` 才占列，`fullscreen` 走覆盖层不占列。
 */
import type { BreakDecl, RightbarForm } from "./shellDecl";

/** 列宽常量（唯一来源；与 token 同值，见文件头纪律） */
export const COLUMN = {
  /** 左栏展开宽（token `--xg-sidebar-default`） */
  sidebarDefault: 280,
  /** 左栏最小宽（token `--xg-sidebar-min`） */
  sidebarMin: 264,
  /** 左栏最大宽（token `--xg-sidebar-max`） */
  sidebarMax: 420,
  /** 折叠轨宽（token `--xg-sidebar-collapsed`） */
  rail: 56,
  /** 右栏最小宽（token `--xg-rightbar-min`） */
  rightbarMin: 300,
  /** 右栏默认占比（token `--xg-rightbar-default-ratio`） */
  rightbarRatio: 0.45,
  /** 右栏上限占比（token `--xg-rightbar-max-ratio`） */
  rightbarMaxRatio: 0.7,
  /** 中栏保护宽（token `--xg-center-min`；中栏不得被挤到 400 以下） */
  centerMin: 400,
} as const;

export interface ColumnInputs {
  /** 断点声明（唯一来源 `shell.decl.yaml`） */
  break: BreakDecl;
  /** 左栏是否折叠（resident／collapsible 档由用户态决定；rail 档恒折叠） */
  sidebarCollapsed: boolean;
  /** 右栏是否展开（**默认收合**） */
  barOpen: boolean;
  /** 右栏形态：`push` 占列／`fullscreen` 覆盖层 */
  barForm: RightbarForm;
  /** 视口宽（px） */
  width: number;
}

export interface Columns {
  /** 左栏占列宽（px；0 ＝ 不占列，走覆盖态） */
  sidebar: number;
  /** 右栏占列宽（px；0 ＝ 不占列） */
  rightbar: number;
  /** 直接可用的 `grid-template-columns` 值 */
  template: string;
}

const clamp = (value: number, lo: number, hi: number): number => Math.min(Math.max(value, lo), hi);

/** 列宽求解（纯函数，可单测） */
export function computeColumns(input: ColumnInputs): Columns {
  const mode = input.break.sidebar;
  // 覆盖态（抽屉／轨的展开态）不占列；手机档恒单栏
  const overlaySidebar = mode === "drawer";
  let sidebar = 0;
  if (!overlaySidebar) {
    if (mode === "rail") sidebar = COLUMN.rail;
    else sidebar = input.sidebarCollapsed ? COLUMN.rail : clamp(COLUMN.sidebarDefault, COLUMN.sidebarMin, COLUMN.sidebarMax);
  }

  // 右栏默认收合；仅 push 且展开时占列（折叠零代价）。
  // 中栏保护：右栏不得把中栏挤到 `centerMin` 以下（102 ConversationRoot 的反面教训＝窄列横向溢出）；
  //   空间不足时右栏降到 `rightbarMin` 仍不够 ⇒ 不占列（改走 fullscreen 覆盖态）。
  const pushAllowed = input.break.rightbar_forms.includes("push");
  const avail = input.width - sidebar - COLUMN.centerMin;
  let rightbar = 0;
  if (input.barOpen && pushAllowed && input.barForm === "push" && avail >= COLUMN.rightbarMin) {
    const want = Math.round(input.width * COLUMN.rightbarRatio);
    const cap = Math.min(Math.round(input.width * COLUMN.rightbarMaxRatio), avail);
    rightbar = clamp(want, COLUMN.rightbarMin, cap);
  }

  const template = `${sidebar}px minmax(0, 1fr) ${rightbar}px`;
  return { sidebar, rightbar, template };
}
